import React, { useState, useEffect } from 'react';
import { Typography, Box, TextField, Paper, Button } from '@mui/material';
import { FieldMapping } from '../../services/fieldMapping.service';
// import { isFieldHidden } from '../../utils/formUtils'; // TODO: Uncomment when implementing field hiding
import SignatureCanvas from '../SignatureCanvas';

interface SignaturesProps {
  data?: any;
  onChange?: (data: any) => void;
  formData?: any;
  onFormDataChange?: (data: any) => void;
  fieldMappings?: FieldMapping[];
  isRequired?: boolean;
  readOnly?: boolean;
  onSubmit?: () => void;
}

interface SignatureData {
  evaluee_signature_date: string;
  evaluee_signature: string;
  evaluee_signature_name: string;

  submitter_signature_date: string;
  submitter_signature: string;
  submitter_name: string;
}

const Signatures: React.FC<SignaturesProps> = ({
  data,
  onChange,
  formData: initialFormData,
  onFormDataChange,
  fieldMappings = [], // eslint-disable-line @typescript-eslint/no-unused-vars
  readOnly = false,
  onSubmit,
}) => {
  // Use new props if available, otherwise fall back to old props
  const actualFormData = initialFormData || data || {};
  const actualOnChange =
    onFormDataChange ||
    onChange ||
    ((_data: any) => {
      /* noop */
    });

  // Helper function to check if a field should be hidden (not yet implemented)
  // const isFieldHiddenLocal = (fieldName: string): boolean => {
  //   return isFieldHidden(fieldName, 'signatures', fieldMappings);
  // };

  const [formData, setFormData] = useState<SignatureData>({
    evaluee_signature_date: '',
    evaluee_signature: '',
    evaluee_signature_name: '',

    submitter_signature_date: '',
    submitter_signature: '',
    submitter_name: '',
    ...actualFormData,
  });

  useEffect(() => {
    setFormData((prev: SignatureData) => ({ ...prev, ...actualFormData }));
  }, [actualFormData]);

  const handleChange = (field: string, value: any) => {
    if (readOnly) return; // Prevent changes in read-only mode
    const updatedData = { ...formData, [field]: value };
    setFormData(updatedData);
    actualOnChange(updatedData);
  };

  return (
    <Box>
      <Typography
        variant='h5'
        gutterBottom
        sx={{ fontWeight: 'bold', color: '#1976d2' }}
      >
        Signatures
      </Typography>

      <Paper sx={{ p: 3 }}>
        <Typography variant='h6' gutterBottom>
          Final Signatures
        </Typography>

        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          {/* Evaluee Signature */}
          <Box sx={{ flex: 1, minWidth: '400px' }}>
            <Box sx={{ mb: 3 }}>
              <Typography
                variant='subtitle1'
                gutterBottom
                sx={{ fontWeight: 'bold' }}
              >
                Signature of Evaluee
              </Typography>

              <TextField
                fullWidth
                label='Name'
                value={formData.evaluee_signature_name}
                onChange={e =>
                  handleChange('evaluee_signature_name', e.target.value)
                }
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                type='date'
                label='Date'
                value={formData.evaluee_signature_date}
                onChange={e =>
                  handleChange('evaluee_signature_date', e.target.value)
                }
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 2 }}
              />

              <SignatureCanvas
                value={formData.evaluee_signature}
                onChange={signature =>
                  handleChange('evaluee_signature', signature)
                }
                label='Evaluee Signature'
                width={350}
                height={120}
              />
            </Box>
          </Box>

          {/* Submitter Signature */}
          <Box sx={{ flex: 1, minWidth: '400px' }}>
            <Box sx={{ mb: 3 }}>
              <Typography
                variant='subtitle1'
                gutterBottom
                sx={{ fontWeight: 'bold' }}
              >
                Signature of the Person Submitting this Form
              </Typography>

              <TextField
                fullWidth
                label='Name of the Person Submitting this Form (print)'
                value={formData.submitter_name}
                onChange={e => handleChange('submitter_name', e.target.value)}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                type='date'
                label='Date'
                value={formData.submitter_signature_date}
                onChange={e =>
                  handleChange('submitter_signature_date', e.target.value)
                }
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 2 }}
              />

              <SignatureCanvas
                value={formData.submitter_signature}
                onChange={signature =>
                  handleChange('submitter_signature', signature)
                }
                label='Submitter Signature'
                width={350}
                height={120}
              />
            </Box>
          </Box>
        </Box>

        {/* Only show submit button if onSubmit is provided (for backward compatibility) */}
        {onSubmit && (
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Button
              variant='contained'
              color='primary'
              size='large'
              onClick={onSubmit}
              sx={{ px: 4, py: 1.5 }}
            >
              Submit Form
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default Signatures;
